/**
 * Кнопка ⓘ и поповер с объяснением — docs/release-2026-09-19.md §1: «никакого мелкого текста
 * ради объяснений». Всё, что объясняет действие, живёт здесь, рядом с ним, а в теле экрана
 * остаются только данные и действия.
 *
 * ПОЧЕМУ ПО НАЖАТИЮ, А НЕ ПО НАВЕДЕНИЮ, КАК `.tip` У ОРГАНОВ ШАПКИ. Подсказка органа отвечает
 * на «что сейчас стоит» и полезна мимоходом; поповер отвечает на «что будет, если нажать» и
 * читается целиком, по просьбе. Всплывающий от случайного наведения абзац посреди диалога
 * закрывал бы соседние поля ровно тогда, когда человек к ним тянется.
 *
 * ПОЧЕМУ `position: fixed` С КООРДИНАТАМИ ОТ КНОПКИ. Поповеры стоят внутри `Modal`, у карточки
 * которого `overflow-y: auto`: абсолютный блок у правого края карточки уезжал бы под её
 * прокрутку, и объяснение приходилось бы искать горизонтальным скроллом. Фиксированный блок
 * меряется от окна и прижимается к его краям; при прокрутке и смене размера окна он
 * закрывается — висящий в пустоте абзац хуже закрытого.
 *
 * Содержимое в DOM только пока поповер открыт: закрытый поповер — это кнопка с именем
 * (`aria-label`), и скринридер не зачитывает объяснение, которого на экране нет.
 */

import {
  type JSX,
  type ReactNode,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import styles from './InfoPopover.module.css';
import { IconInfo } from './icons.tsx';

/** Ширина — та же, что у `.tip` органов шапки: два вида подсказок одной геометрии. */
const WIDTH = 300;
/** Поле от края окна. */
const EDGE = 8;
/** Зазор между кнопкой и поповером. */
const GAP = 8;

/** Ширина поповера в этом окне: на телефоне 300px не помещаются вместе с полями. */
const popWidth = (): number => Math.max(0, Math.min(WIDTH, window.innerWidth - 2 * EDGE));

interface Place {
  readonly top: number;
  readonly left: number;
}

export interface InfoPopoverProps {
  /** Имя кнопки для клавиатуры и скринридера: «Что такое сравнение стилей». */
  readonly label: string;
  readonly children: ReactNode;
}

export function InfoPopover({ label, children }: InfoPopoverProps): JSX.Element {
  const popId = useId();
  const [open, setOpen] = useState(false);
  const [place, setPlace] = useState<Place | null>(null);
  const wrapRef = useRef<HTMLSpanElement | null>(null);
  const btnRef = useRef<HTMLButtonElement | null>(null);
  const popRef = useRef<HTMLSpanElement | null>(null);

  /*
   * Место считается ДО отрисовки кадра (layout-эффект), по уже разложенному, но ещё
   * невидимому поповеру той же ширины: иначе он мигнул бы в углу окна, а высоту его до
   * раскладки не знает никто. Под кнопкой, а если нижний край уходит за окно — над ней.
   */
  useLayoutEffect(() => {
    if (!open) {
      setPlace(null);
      return;
    }
    const btn = btnRef.current;
    if (btn === null) return;
    const r = btn.getBoundingClientRect();
    const width = popWidth();
    const left = Math.min(Math.max(EDGE, r.left), Math.max(EDGE, window.innerWidth - width - EDGE));
    const h = popRef.current?.getBoundingClientRect().height ?? 0;
    const below = r.bottom + GAP;
    const above = r.top - GAP - h;
    const top = below + h > window.innerHeight - EDGE && above >= EDGE ? above : below;
    setPlace({ top, left });
  }, [open]);

  // Закрывается щелчком мимо, прокруткой, сменой размера окна и Escape.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent): void => {
      const wrap = wrapRef.current;
      if (wrap !== null && e.target instanceof Node && wrap.contains(e.target)) return;
      setOpen(false);
    };
    const onAway = (): void => {
      setOpen(false);
    };
    /*
     * Escape закрывает поповер, а не диалог под ним. `Modal` слушает keydown на `document` во
     * всплытии; этот слушатель стоит там же, но на ПОГРУЖЕНИИ, то есть раньше, и дальше
     * событие не пускает. Второй Escape закроет уже диалог.
     */
    const onKey = (e: KeyboardEvent): void => {
      if (e.key !== 'Escape') return;
      e.stopPropagation();
      setOpen(false);
      btnRef.current?.focus();
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey, true);
    window.addEventListener('resize', onAway);
    window.addEventListener('scroll', onAway, true);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey, true);
      window.removeEventListener('resize', onAway);
      window.removeEventListener('scroll', onAway, true);
    };
  }, [open]);

  return (
    <span ref={wrapRef} className={styles['wrap']}>
      <button
        ref={btnRef}
        type="button"
        className={open ? `${styles['btn']} ${styles['btnOn']}` : styles['btn']}
        aria-label={label}
        aria-expanded={open}
        aria-controls={open ? popId : undefined}
        onClick={() => {
          setOpen((v) => !v);
        }}
      >
        <IconInfo width={14} height={14} />
      </button>
      {open ? (
        <span
          ref={popRef}
          id={popId}
          role="note"
          aria-label={label}
          className={styles['pop']}
          style={
            place === null
              ? { visibility: 'hidden', top: 0, left: 0, width: popWidth() }
              : { top: place.top, left: place.left, width: popWidth() }
          }
        >
          {children}
        </span>
      ) : null}
    </span>
  );
}
